import {Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  isLikePrivateAddress,
  parseDomainName,
} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ServerAuthenticationStrategiesEnum} from '../../../models/api/serverModel';
import {ServerEditorState} from '../../editors/serverEditor/serverEditorState';

/*
 * Types.
 */

export enum ServerEditorFormErrorsEnum {
  MISSING_ORIGIN = 'missing_origin',
  INVALID_DOMAIN_ORIGIN = 'invalid_domain_origin',
  INVALID_ORIGIN = 'invalid_origin',
  INVALID_AUTHENTICATION_CONFIG = 'invalid_authentication_config',
}

type ServerChecker = (state: ServerEditorState) => ServerEditorFormErrorsEnum | undefined;

/*
 * Checkers.
 */

const ERROR_CHECKS: ReadonlyArray<ServerChecker> = [
  // Origin.
  ({serverState}) => {
    const {origin} = serverState;

    if (!origin) {
      return ServerEditorFormErrorsEnum.MISSING_ORIGIN;
    }

    try {
      const url = new URL(origin);
      if (!parseDomainName(url.host) || isLikePrivateAddress(url.host)) {
        return ServerEditorFormErrorsEnum.INVALID_DOMAIN_ORIGIN;
      }

      return undefined;
    } catch (err) {
      return ServerEditorFormErrorsEnum.INVALID_ORIGIN;
    }
  },

  // Authentication config.
  ({serverState}) => {
    if (!serverState?.isWritable) {
      return undefined;
    }

    const {authenticationConfig} = serverState;

    switch (authenticationConfig.strategy) {
      case ServerAuthenticationStrategiesEnum.OAUTH2:
        return authenticationConfig.clientId &&
          authenticationConfig.clientSecret &&
          authenticationConfig.scope &&
          authenticationConfig.tokenUrl
          ? undefined
          : ServerEditorFormErrorsEnum.INVALID_AUTHENTICATION_CONFIG;

      default:
        return undefined;
    }
  },
];

/*
 * Helpers.
 */

export function computeServerEditorFormErrors(
  state: ServerEditorState,
): ReadonlyArray<ServerEditorFormErrorsEnum> {
  return ERROR_CHECKS.map((checker) => checker(state)).filter(isDefined);
}

/*
 * Labels.
 */

export const serverEditorFormErrorLabels: Record<ServerEditorFormErrorsEnum, ReactNode> = {
  [ServerEditorFormErrorsEnum.MISSING_ORIGIN]: (
    <Trans id="server-editor-footer-tooltip-warning-missing-origin">
      Your server needs to have an origin
    </Trans>
  ),

  [ServerEditorFormErrorsEnum.INVALID_DOMAIN_ORIGIN]: (
    <Trans id="server-editor-footer-tooltip-warning-invalid-domain-origin">
      Your server needs to target a valid domain for its origin
    </Trans>
  ),

  [ServerEditorFormErrorsEnum.INVALID_ORIGIN]: (
    <Trans id="server-editor-footer-tooltip-warning-invalid-origin">
      Your server needs to target a valid origin
    </Trans>
  ),

  [ServerEditorFormErrorsEnum.INVALID_AUTHENTICATION_CONFIG]: (
    <Trans id="server-editor-footer-tooltip-warning-invalid-authentication-config">
      Your server needs to have a valid authentication config
    </Trans>
  ),
};
